import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.background};
`;

const Section = styled.section`
  margin-bottom: 30px;
`;

const Heading = styled.h2`
  margin-bottom: 15px;
  text-align: center; /* Center the section titles */
`;

const SubHeading = styled.h3`
  margin-top: 20px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.headerText};
`;

const CleanList = styled.ul`
  list-style-type: none; /* This will hide dots */
  margin-left: 20px; /* Adjust as needed, you might want less indent for dotless lists */
  padding-left: 0; /* Important to remove default padding that creates indent */
  margin-bottom: 15px;

  a{
  color: ${({ theme }) => theme.headerText};}
`;

const Paragraph = styled.p`
  margin-bottom: 10px;
  line-height: 1.6;
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 20px;
  margin-bottom: 15px;
`;

const ListItem = styled.li`
  margin-bottom: 5px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
  margin-bottom: 30px;
`;

const Th = styled.th`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
`;

const Td = styled.td`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
`;

const Image = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
  margin: 1.5em auto;
  border: 1px solid #ddd;
  box-shadow: 2px 2px 5px rgba(0,0,0,0.1);
`;

// Helper component for displaying the fields in a table
// This is a generic version; adapt it if your CoreTemplateTable/DrugTemplateTable are more specific
const TemplateFieldsTable = ({ title, fields }) => (
  <Section>
    <SubHeading>{title}</SubHeading>
    <Table>
      <thead>
        <tr>
          <Th>Column #</Th>
          <Th>Classify Field Setup</Th>
          <Th>Target Field Category</Th>
          <Th>Target Column Name</Th>
          <Th>Target Column Type</Th>
          <Th>Source Column Name</Th>
          <Th>Column Not Used</Th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field, index) => (
          <tr key={index}>
            <Td>{field.column_number}</Td>
            <Td>{field.target_column_classify_field_setup}</Td>
            <Td>{field.target_field_category}</Td>
            <Td>{field.target_column_name}</Td>
            <Td>{field.target_column_type}</Td>
            <Td>{field.source_column_name}</Td>
            <Td>{field.column_not_used}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  </Section>
);

const Cvc2019Content = () => {
  // Data for the Core Template Fields table
  const coreTemplateFields = [
    {
      column_number: 1,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_survey_production_lot_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_survey_production_lot_id',
    },
    {
      column_number: 2,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_survey_main_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_survey_main_id',
    },
    {
      column_number: 3,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_record_group_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_record_group_id',
    },
    {
      column_number: 4,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_account_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_account_id',
    },
    {
      column_number: 5,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_survey_production_lot_upload_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_survey_production_lot_upload_id',
    },
    {
      column_number: 6,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_template_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_pl_template_id',
    },
    {
      column_number: 7,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_production_lot_code',
      target_column_type: 'varchar(128)',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'feedlot_lot_name',
    },
    {
      column_number: 8,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_sex_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
      source_column_name: 'sex',
    },
    {
      column_number: 9,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_animal_age_category_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
      source_column_name: 'cattle_type',
    },
    {
      column_number: 10,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_percent_calves',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'percent_calves',
    },
    {
      column_number: 11,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_percent_yearlings',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'percent_yearlings',
    },
    {
      column_number: 12,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_origin_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
      source_column_name: 'origin',
    },
    {
      column_number: 13,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_brd_risk_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
      source_column_name: 'brd_risk',
    },
    {
      column_number: 14,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_n_animals_in',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'hd_in',
    },
    {
      column_number: 15,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_avg_date_in',
      target_column_type: 'date',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'avg_date_in',
    },
    {
      column_number: 16,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_source_weight_in_units_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
      source_column_name: 'csb_pl_source_weight_in_units_id',
    },
    {
      column_number: 17,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_source_weight_in_value',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_pl_source_weight_in_value',
    },
    {
      column_number: 18,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_avg_weight_in_kgs',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Multiple Target Fields (3)',
      source_column_name: 'avg_wt_in',
    },
    {
      column_number: 19,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_avg_date_out',
      target_column_type: 'date',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'avg_date_out',
    },
    {
      column_number: 20,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_quarter_of_year_closeout_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
      source_column_name: 'season',
    },
    {
      column_number: 21,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_source_weight_out_units_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
      source_column_name: 'csb_pl_source_weight_out_units_id',
    },
    {
      column_number: 22,
      column_not_used: 'Not Used (1)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_source_weight_out_value',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'csb_pl_source_weight_out_value',
    },
    {
      column_number: 23,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_avg_weight_out_kgs',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Multiple Target Fields (3)',
      source_column_name: 'avg_wt_out',
    },
    {
      column_number: 24,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_n_animals_dead',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'deaths',
    },
    {
      column_number: 25,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_n_animals_slaughtered',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'hd_out',
    },
    {
      column_number: 26,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_total_days_on_feed',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'days_on_feed',
    },
    {
      column_number: 27,
      column_not_used: 'Used (0)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_total_head_days',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
      source_column_name: 'hddays',
    },
  ];

  // Data for the Drug Template Fields table
  const drugTemplateFields = [
    {
      column_number: 1,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_survey_production_lot_drug_id',
      source_column_type: 'bigint',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_survey_production_lot_drug_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 2,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_survey_production_lot_id',
      source_column_type: 'bigint',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_survey_production_lot_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 3,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_survey_main_id',
      source_column_type: 'bigint',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_survey_main_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 4,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_record_group_id',
      source_column_type: 'bigint',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_record_group_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 5,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_account_id',
      source_column_type: 'bigint',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_account_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 6,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_survey_production_lot_upload_id',
      source_column_type: 'bigint',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_survey_production_lot_upload_id',
      target_column_type: 'bigint',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 7,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_pl_template_id',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_template_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 8,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_pl_drug_ordinal',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_ordinal',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 9,
      column_not_used: 'Used (0)',
      source_column_name: 'route',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_route_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
    },
    {
      column_number: 10,
      column_not_used: 'Used (0)',
      source_column_name: 'active_ingredient',
      source_column_type: 'varchar (192)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_source_name',
      target_column_type: 'varchar (192)',
      target_column_classify_field_setup: 'Setup Standard Field (0)',
    },
    {
      column_number: 11,
      column_not_used: 'Used (0)',
      source_column_name: 'amount',
      source_column_type: 'numeric',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_source_drug_value',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 12,
      column_not_used: 'Used (0)',
      source_column_name: 'units',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_source_drug_value_units_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
    },
    {
      column_number: 13,
      column_not_used: 'Used (0)',
      source_column_name: 'product',
      source_column_type: 'integer',
      target_field_category: 'Trade Name (2)',
      target_column_name: 'csb_pl_drug_trade_name_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
    },
    {
      column_number: 14,
      column_not_used: 'Used (0)',
      source_column_name: 'active_ingredient',
      source_column_type: 'integer',
      target_field_category: 'Active Ingredient (1)',
      target_column_name: 'csb_pl_drug_active_ingredient_id',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
    },
    {
      column_number: 15,
      column_not_used: 'Not Used (1)',
      source_column_name: 'amount',
      source_column_type: 'numeric',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_total_mg',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 16,
      column_not_used: 'Not Used (1)',
      source_column_name: 'csb_pl_drug_total_ml',
      source_column_type: 'numeric',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_total_ml',
      target_column_type: 'numeric',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 17,
      column_not_used: 'Used (0)',
      source_column_name: 'reason_for_use',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_primary_reason_for_use',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Lookup Field (2)',
    },
    {
      column_number: 18,
      column_not_used: 'Used (0)',
      source_column_name: 'condition',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_respiratory',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 19,
      column_not_used: 'Not Used (1)',
      source_column_name: 'condition',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_digestive',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 20,
      column_not_used: 'Not Used (1)',
      source_column_name: 'condition',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_lameness',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 21,
      column_not_used: 'Not Used (1)',
      source_column_name: 'condition',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_neurologic',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 22,
      column_not_used: 'Not Used (1)',
      source_column_name: 'condition',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_mixed',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 23,
      column_not_used: 'Not Used (1)',
      source_column_name: 'condition',
      source_column_type: 'varchar (128)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_mixed_description',
      target_column_type: 'varchar (128)',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 24,
      column_not_used: 'Not Used (1)',
      source_column_name: 'condition',
      source_column_type: 'integer',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_other',
      target_column_type: 'integer',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
    {
      column_number: 25,
      column_not_used: 'Not Used (1)',
      source_column_name: 'condition',
      source_column_type: 'varchar (128)',
      target_field_category: 'Core Field (0)',
      target_column_name: 'csb_pl_drug_secondary_reason_other_description',
      target_column_type: 'varchar (128)',
      target_column_classify_field_setup: 'Setup Standard Field (1)',
    },
  ];

  return (
    <Container>
      <Section>
        <Heading>
          Production Lot Upload Data Preparation: Coaldale Veterinary Clinic (CVC)
          <br />
          Site Information Details
        </Heading>
        <Paragraph>Coaldale Veterinary Clinic (3) (CVC)</Paragraph>
        <Paragraph>Vet: CSBV00004 (4)</Paragraph>
        <Paragraph>Project Year: 2019</Paragraph>
        <Paragraph>The site has four (4) herds. They are:</Paragraph>
        <List>
          <ListItem>CSBH0000030</ListItem>
          <ListItem>CSBH0000031</ListItem>
          <ListItem>CSBH0000032</ListItem>
          <ListItem>CSBH0000033</ListItem>
        </List>
        <Paragraph>Access to current years documentation click on the appropriate link below:</Paragraph>
        <List>
          <CleanList><Link to="/beef/data-preparation/cvc2020">CVC 2020</Link></CleanList>
          <CleanList><Link to="/beef/data-preparation/cvc2021">CVC 2021</Link></CleanList>
          <CleanList><Link to="/beef/data-preparation/cvc2022">CVC 2022</Link></CleanList>
          <CleanList><Link to="/beef/data-preparation/cvc2023">CVC 2023</Link></CleanList>
          <CleanList><Link to="/beef/data-preparation/cvc2024">CVC 2024</Link></CleanList>
        </List>
      </Section>

      <Section>
        <Heading>
          Production Lot Upload Data Preparation: Coaldale Veterinary Clinic (CVC)
          <br />
          Data Source Information
        </Heading>
        <Paragraph>CVC has 4 herds.</Paragraph>
        <Paragraph>Each spreadsheet/workbook from CVC represents all herds for a specific sampling year.</Paragraph>
        <Paragraph>The core fields in a sheet have drug fields listed at the end of the core field data.</Paragraph>
        <Paragraph>The drugs are listed as rows, not columns, within the sheet.</Paragraph>

        <Paragraph>Core field data is repeated for each associated row of drug information.</Paragraph>
        <Paragraph>However, the repeated core field data is implied, meaning core field information has 1 row filled in, the remaining rows are blank until you switch to a different production lot.</Paragraph>
        <Paragraph>During the upload of the spreadsheet into the database, missing or implied core field data is filled in.</Paragraph>

        <Paragraph>The rows of drug data represent 1 drug, usually a product name and active ingredient for 1 drug route.</Paragraph>
        <Paragraph>All drug routes and associated drugs for a production lot are available.</Paragraph>

        <Paragraph>There are several extra things you should do in the spreadsheet before uploading the data:</Paragraph>
        <Paragraph>The **season** field is not added and needs to be calculated.</Paragraph>
        <Paragraph>Define season using the **Average Date Out** field in the following way:</Paragraph>
        <List>
          <ListItem>Winter &rarr; January 1 thru March 31</ListItem>
          <ListItem>Spring &rarr; April 1 thru June 30</ListItem>
          <ListItem>Summer &rarr; July 1 thru September 30</ListItem>
          <ListItem>Fall &rarr; October 1 thru December 31</ListItem>
        </List>

        <Paragraph>The **days on feed** field needs to be calculated.</Paragraph>
        <Paragraph>Define days on feed &rarr; Average Date Out minus (-) Average Date In</Paragraph>

        <Paragraph>Before creating and setting up a template, go thru all spreadsheets provided from the site.</Paragraph>
        <Paragraph>Collect field names from each site and put them together. Review the field names to make sure they all match. My experience is the field names do not match. To reduce the number of templates to create, get the field names to match. The fields do not need to be in the same order.</Paragraph>
      </Section>

      <Section>
        <Heading>
          Production Lot Upload Data Preparation: Coaldale Veterinary Clinic (CVC)
          <br />
          Template Setup Suggestions
        </Heading>
        <Paragraph>A two template setup is used for one or more years batch of data.</Paragraph>
        <Paragraph>For the current document, assume we place all known drug fields for all drug routes at the end of the core fields.</Paragraph>
        <Paragraph>Since the data switches from Core field columns to Drug fields as rows we insert a field in the spreadsheet</Paragraph>
        <Paragraph>The field is called "change_to_rows". It is a column header in the spreadsheet and a field name in the uploaded data table.</Paragraph>
        <Paragraph>When we do this, the primary, top level template created represents the Core fields.</Paragraph>
        <Paragraph>The secondary, second level child template created represents the Drug fields associated with a Core row of data.</Paragraph>

        <Paragraph>The Core Template is populated from the Target Table from the production lot table (core fields).</Paragraph>
        <Paragraph>The Child Drug Template is populated from the Target Table from the drug table.</Paragraph>
        <Paragraph>Here are the typical template settings used for CVC.</Paragraph>
        <List>
          <ListItem><strong>Name:</strong> CVC 2019 - Core</ListItem>
          <ListItem><strong>Level:</strong> Top Level</ListItem>
          <ListItem><strong>Type:</strong> Core</ListItem>
          <ListItem><strong>Route:</strong> Multiple Drugs</ListItem>
          <ListItem><strong>Version:</strong> Version 1</ListItem>
          <ListItem><strong>By Row or Column:</strong> By Column</ListItem>
          <ListItem><strong>Method:</strong> Populate template fields from Target</ListItem>
        </List>
        <Image src="/images/ss_template_cvc_core_2019.png"/>
      </Section>

      <TemplateFieldsTable
        title="Core Template: Associated Core Template Fields"
        fields={coreTemplateFields}
      />

      <Section>
        <List>
          <ListItem><strong>Name:</strong> CVC 2019 - Drugs</ListItem>
          <ListItem><strong>Level:</strong> Second Level</ListItem>
          <ListItem><strong>Type:</strong> Drug</ListItem>
          <ListItem><strong>Route:</strong> Multiple Drugs</ListItem>
          <ListItem><strong>Version:</strong> Version 1</ListItem>
          <ListItem><strong>By Row or Column:</strong> By Row</ListItem>
          <ListItem><strong>Method:</strong> Populate template fields from Target</ListItem>
        </List>
        <Image src="/images/ss_template_cvc_drug_2019.png"/>
      </Section>

      <TemplateFieldsTable
        title="Drug Template: Associated Template Fields"
        fields={drugTemplateFields}
      />
    </Container>
  );
};

export default Cvc2019Content;